<script setup>
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import {ref, computed} from "vue";

    const emit = defineEmits([
        'invoice-generated',
        'request-errors',
    ]);

    const props = defineProps({
        payoutId: {
            type: [Number, String]
        },
        fromCompany: {type: Object, default: {}},
        toCompany: {type: Object, default: {}},
        payoutMethod: {
            type: String,
            default: 'paypal',
        },
        invoiceNumber: {type: [Number,String], required: true},
        items: {
            type: Array,
            required: true,
            default: () => [],
        },
        currency: {
            type: String,
            required: true,
            default: 'usd',
        },
        amountWithCurrencyFormatted: {
            type: String,
            required: true,
            default: 'USD 0.00',
        },
        notes: {type: String, required: false, default: ''},
        terms: {type: String, required: false, default: ''},
        tax: {type: Number, required: false, default: 0},
    })

    const portalContentAndDesignStore = usePortalContentAndDesignStore()
    const generatingInvoice = ref(false)

    const todayDate = () => {
            return formatDate(new Date());
        }

    const todayPlus20 = () => {
            const today = new Date();
            today.setDate(today.getDate() + 20);
            return formatDate(today);
        }
    const formatDate = (date) => {
            return new Intl.DateTimeFormat('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }).format(date);
        }

    const payoutMethodName = computed(() => {
        switch (props.payoutMethod) {
            case 'bank_transfer':
                return 'Bank Transfer';
            case 'paypal':
                return 'PayPal';
            case 'crypto':
                return 'Crypto';
            default:
                return '';
        }
    });

    const amountWithoutCurrency = computed(() => {
        const cleanedStr = props.amountWithCurrencyFormatted.replace(/[^0-9.,]/g, '');
        return parseFloat(cleanedStr.replace(/,/g, ''));
    });

    const generateInvoice = () => {
            generatingInvoice.value = true;
            axios.post(route('partners_int.payouts.invoice-file-generate', {
                payoutId: props.payoutId,
                payoutMethod: payoutMethodName.value,
                invoiceNumber: props.invoiceNumber,
                fromCompany: props.fromCompany,
                toCompany: props.toCompany,
                date: todayDate(),
                dueDate: todayPlus20(),
                amount: amountWithoutCurrency.value,
                currency: props.currency,
            }))
                .then(response => {
                    emit('request-errors', {});
                    const status = parseInt(response.data.status ?? -1);
                    if (status === 1) {
                        emit('invoice-generated', {
                            invoiceUrl: response.data.invoice.url,
                            invoicePath: response.data.invoice.path,
                        });
                    }
                    if (response.data.status === 0) {
                        emit('request-errors', {error: [response.data.message || 'Something went wrong.']});
                    }
                    generatingInvoice.value = false;
                })
                .catch(error => {
                    if (error.response?.status === 422 && error.response?.data?.error) {
                        emit('request-errors', {error: [error.response.data.error]});
                    }
                    else if (error.response && error.response.data && error.response?.data?.errors) {
                        emit('request-errors', error.response.data.errors)
                    }
                    generatingInvoice.value = false;
                })
                .finally(() => generatingInvoice.value = false);
        }
</script>
<template>
    <div class="invoice-container min-w-md mx-auto p-6 bg-white border border-gray-200 rounded-lg shadow text-sm">
        <!-- Header Section -->
        <div class="flex justify-between mb-6">
            <div class="flex flex-col">
                <div class="font-semibold">{{ fromCompany.name || '' }}</div>
                <div v-if="fromCompany.email">{{ fromCompany.email }}</div>
                <div>{{ fromCompany.address || '' }}</div>
                <div v-if="fromCompany.country">{{ fromCompany.country }}</div>
                <div>Payout method: {{ payoutMethodName }}</div>
            </div>
            <div class="flex justify-end self-start items-center space-x-2">
                <h1 class="font-bold">INVOICE</h1>
                <div class="font-semibold">#{{ invoiceNumber }}</div>
            </div>
        </div>

        <div class="flex justify-between mb-6">
            <!-- Billing Information -->
            <div>
                <p class="mb-1 text-gray-500">Bill To:</p>
                <div class="text-sm">
                    <span class="font-semibold">{{ toCompany.company_name }}</span><br>
                    {{ toCompany.address }}<br>
                    {{ toCompany.zip ? toCompany.zip + ', ' : '' }}{{ toCompany.city ? toCompany.city + ', ' : '' }} {{ toCompany.country }}
                    <template v-if="toCompany.registration_number"><br>Reg. no: {{ toCompany.registration_number }}</template>
                    <template v-if="toCompany.vat_number"><br>VAT no: {{ toCompany.vat_number }}</template>
                </div>
            </div>

            <!-- Company and Date Details -->
            <div class="flex justify-between items-center mb-6">
                <div class="text-right text-gray-500">
                    <div>Date: <span class="font-semibold">{{ todayDate() }}</span></div>
                    <div>Due Date: <span class="font-semibold">{{ todayPlus20() }}</span></div>
                    <div class="bg-gray-200">Balance Due: <span class="font-semibold">{{ amountWithCurrencyFormatted }}</span></div>
                </div>
            </div>
        </div>

        <!-- Items Table -->
        <div class="mb-6">
            <p class="font-semibold mb-3">Invoice Items</p>
                <div class="grid grid-cols-4 bg-gray-800 text-left text-white rounded">
                    <div class="py-1 px-4 border-b border-gray-300">Item</div>
                    <div class="py-1 px-4 border-b border-gray-300">Quantity</div>
                    <div class="py-1 px-4 border-b border-gray-300">Rate</div>
                    <div class="py-1 px-4 border-b border-gray-300">Amount</div>
                </div>
                <div v-for="(item, index) in items" :key="index" class="grid grid-cols-4">
                    <div class="py-2 px-4 border-b">{{ item.name }}</div>
                    <div class="py-2 px-4 border-b">{{ item.quantity }}</div>
                    <div class="py-2 px-4 border-b">{{ amountWithoutCurrency }}</div>
                    <div class="py-2 px-4 border-b">{{ amountWithCurrencyFormatted }}</div>
                </div>
        </div>

        <!-- Totals Section -->
        <div class="text-right mb-6 text-gray-500">
            <div>Total: <span class="font-semibold">{{ amountWithCurrencyFormatted }}</span></div>
        </div>

        <!-- Notes and Terms -->
        <div class="mb-6">
            <p class="font-semibold text-gray-500 mb-1">Notes:</p>
            <p class="text-gray-700">{{ notes }}</p>
        </div>

        <div class="mb-4">
            <p class="font-semibold text-gray-500 mb-1">Terms:</p>
            <p class="text-gray-700">{{ terms }}</p>
        </div>

        <hr/>

        <div class="mt-4">
            <p class="font-semibold text-gray-500 mb-1 text-xs">Note: </p>
            <p class="text-gray-500 text-xs mb-4">
                {{
                    portalContentAndDesignStore.translationSettings.portal_translation_general_invoice_preview_note ||
                    'You’re viewing a preview of the invoice. After generating it, you can easily view or download it as a PDF.'
                }}
            </p>

            <btn @click="generateInvoice()"
                 class="w-full mt-1"
                 :disabled="generatingInvoice"
                 :loading="generatingInvoice"
            >
                {{
                     generatingInvoice
                     ? (portalContentAndDesignStore.translationSettings.portal_translation_general_generating_invoice || 'Generating invoice…')
                     : (portalContentAndDesignStore.translationSettings.portal_translation_general_generate_invoice || 'Generate & attach an invoice')
                }}
            </btn>

        </div>
    </div>
</template>

<style scoped>
.invoice-container {
    position: absolute;
    width: 31.25rem;
    top: 0;
    right: -31.875rem;
}
</style>
